import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DialogContainer, Grid, View } from "@adobe/react-spectrum";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { SessionInfo } from "../../data/SessionInfo";
import { ListApplicationsRequest } from "../../services/soap/features/requests/ListApplicationsRequest";
import { Application } from "../../services/soap/features/responses/ListApplicationsResponse";
import UserHeader from "./Header/UserHeader";
import LoginDialog from "../../components/protected/Dialogs/LoginDialog";
import { ServiceBase } from "../../services/soap/ServiceBase";
import { useDependency } from "../../contexts/DependencyProvider";
import { MessageCountProvider } from "../../contexts/MessageCountContext";
import { doLogin } from "../../utils/LoginUtils";
import { ToastQueue } from "@react-spectrum/toast";
import { useTranslation } from "react-i18next";
import styles from "./UserWebLayout.module.css";
import { AppMenuMobile } from "../../components/protected/AppMenuMobile/AppMenuMobile";
import { UserHeaderMobile } from "./Header/UserHeaderMobile";
import { getPrimaryPath } from "../../config/app-routes";
import { MobileGlobalSearch } from "../../components/protected/MobileSearch/MobileGlobalSearch";
import { MobileMenu } from "../../components/protected/Menu/MobileMenu";
import { AppMenuItemType } from "../../components/protected/AppMenu/AppMenu.types";
import { AppTabContainerItemType } from "../../components/protected/AppTabs/AppTabContainerItem";
import { usePreloadAssets } from "../../hooks/UsePreloadAssets";
import { addKey, useKeyboardShortcuts } from "../../hooks/UseKeyboardShortcuts";
import { HelpDialog } from "../../components/protected/Dialogs/HelpDialog";
import {
  AtollonEvent,
  unsubscribe,
  subscribe,
} from "../../infra/events/Events";
import { invokeFinderApp } from "../../utils/NavigationUtils";
import { ListLanguagesRequest } from "../../services/soap/translate/reqeusts/ListLanguagesRequest";
import { useReefConfig } from "../../hooks/UseReefConfig";
import { useDispatch } from "react-redux";
import {
  addAppInfo,
  removeAppInfo,
  selectApp,
} from "../../state/userWebLayout/userWebLayoutSlice";
import { UpdateFavouriteContextRequest } from "../../services/soap/project/requests/UpdateFavouriteContextRequest";
import { Profile } from "../../pages/protected/profile/Profile";
import { OneTimeStorage } from "../../services/LocalStorage/OneTimeStorage";
import { LogoutRequest } from "../../services/soap/main/requests/LogoutRequest";
import { useApps } from "../../contexts/AppsProvider";
import { useBeforeRender } from "../../hooks/useBeforeRender";
import NewMessageDialog from "../../pages/protected/new-message/NewMessageDialog";
import CommunicationMenu from "../../pages/protected/communication/CommunicationMenu";

function _MakeGroupsForAppMenu(apiResponse: Application[]): any[] {
  var icons: Map<string, string> = new Map<string, string>([
    ["general", "../assets/icons/group-402.svg"],
    ["marketing", "../assets/icons/group-4021.svg"],
    ["sales", "../assets/icons/group-4022.svg"],
    ["project", "../assets/icons/group-4023.svg"],
    ["services", "../assets/icons/group-4024.svg"],
    ["finance", "../assets/icons/group-4025.svg"],
    ["reports", "../assets/icons/group-403.svg"],
    ["recruitment", "../assets/icons/group-4026.svg"],
  ]);

  let defaultIcon: string = "../assets/icons/group-397.svg";

  let keyMapping: Map<string, string> = new Map<string, string>([
    ["general", "general"],
    ["marketing", "marketing"],
    ["trading", "sales"],
    ["project", "project"],
    ["services", "services"],
    ["finance", "finance"],
    ["reports", "reports"],
    ["recruitment", "recruitment"],
  ]);

  let labelMapping: Map<string, string> = new Map<string, string>([
    ["general", "General"],
    ["marketing", "Marketing"],
    ["trading", "Sales"],
    ["project", "Project"],
    ["services", "Services"],
    ["finance", "Finance"],
    ["reports", "Reports"],
    ["recruitment", "Recruitment"],
  ]);

  let displayOrder: Map<string, number> = new Map<string, number>([
    ["general", 10],
    ["marketing", 20],
    ["sales", 30],
    ["project", 40],
    ["services", 50],
    ["finance", 60],
    ["reports", 70],
    ["recruitment", 80],
    ["other", 90],
  ]);

  const items: AppMenuItemType[] = apiResponse.map((app, index) => ({
    sectionTitle: app.subCategory.trim(),
    itemTitle: app.name ?? "",
    itemIcon:
      keyMapping.has(app.subCategory.trim()) &&
      icons.has(keyMapping.get(app.subCategory.trim())!)
        ? icons.get(keyMapping.get(app.subCategory.trim())!)!
        : defaultIcon,
    itemLink: getPrimaryPath(app),
    itemAppIdent: app.appIdent,
    appId: app.id,
  }));

  const grouped_items = items.reduce((groups: any, item) => {
    const group = item.sectionTitle!;
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(item);
    return groups;
  }, {});

  const groupArrays = Object.keys(grouped_items).map((group) => {
    return {
      sectionKey: group,
      sectionTitle: labelMapping.get(group),
      items: grouped_items[group],
    };
  });

  groupArrays.sort(
    (a: any, b: any) =>
      displayOrder.get(keyMapping.get(a.sectionKey)!)! -
      displayOrder.get(keyMapping.get(b.sectionKey)!)!
  );
  return groupArrays;
}

interface MessageDialogData {
  id: string;
  itemId: string;
  attachments?: any;
  notifyUsers?: any;
  objMessage?: any;
  replyType?: string;
  emailAttachments?: any;
  /** Whether the context is a folder (not a valid project/activity/contact context) */
  isFolderContext?: boolean;
}
function UserWebLayout() {
  const LOGIN_DIALOG = "login-dialog";
  const HELP_DIALOG = "help-dialog";

  /** Code to hide Observer Error */
  useBeforeRender(() => {
    window.addEventListener("error", (e) => {
      if (e) {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div"
        );
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay"
        );
        if (resizeObserverErr)
          resizeObserverErr.className = "hide-resize-observer";
        if (resizeObserverErrDiv)
          resizeObserverErrDiv.className = "hide-resize-observer";
      }
    });
  }, []);

  const {
    mainService,
    featureService,
    translateService,
    projectService,
    store,
  } = useDependency();

  const { apps, setApps, redirectUrl, setRedirectUrl } = useApps();

  const dispatch = useDispatch();

  //const { instanceName } = useLagoonConfig();
  const { loginInfo } = useReefConfig();
  const { state, pathname, search } = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDialog, setShowDialog] = useState("");
  const [isShowSearch, setIsShowSearch] = useState(false);
  const [isProfile, setIsProfile] = useState(false);
  const [isCommMenuOpen, setIsCommMenuOpen] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(true);

  const [groups, setGroups] = useState<any[]>([]);
  const [openedApps, setOpenedApps] = useState<AppTabContainerItemType[]>([]);
  const openedAppsRef = useRef<AppTabContainerItemType[]>([]);
  const [selectedAppKey, setSelectedAppKey] = useState("");
  const [messageDialogs, setMessageDialogs] = useState<MessageDialogData[]>([]);
  const [languagesLoaded, setLanguagesLoaded] = useState(false);
  const { folderStatusMapping, projectStatusMapping, activityStatusMapping } =
    usePreloadAssets();
  const session: SessionInfo = state.session;

  const is_login_in_progress = useRef(false);
  const applicationsLoaded = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    openedAppsRef.current = openedApps;
  }, [openedApps]);

  const { i18n } = useTranslation();

  useEffect(() => {
    //console.log('i18n.language', i18n.language);
    document.body.dir = i18n.dir();
  }, [i18n, i18n.language]);

  // Memoize language loading logic to prevent unnecessary API calls
  const loadLanguages = useMemo(() => {
    return async () => {
      if (languagesLoaded) return; // Skip if already loaded

      try {
        let language_response = await translateService.listLanguages(
          new ListLanguagesRequest(store.Server, store.SessionId)
        );
        if (!language_response || +language_response.count === 0) return;

        const record = language_response.LANGUAGE.find(
          (x: any) => +x.id === +store.session.userPreferences.language
        );

        if (!record) return;

        i18n.changeLanguage(record.isoShort);
        setLanguagesLoaded(true); // Mark as loaded to prevent future calls
      } catch (error) {
        console.log("error in loadLanguages", error);
      }
    };
  }, [
    translateService,
    store.Server,
    store.SessionId,
    store.session.userPreferences.language,
    i18n,
    languagesLoaded,
  ]);

  // Separate useEffect for language loading - only runs once per session
  useEffect(() => {
    loadLanguages();
  }, [loadLanguages]);

  const moveToApp = useCallback(
    (item: AppTabContainerItemType, data: string = "") => {
      let url = item.ref;
      if (data) {
        url = url + "?" + data;
      }

      navigate(url, { state: { ...state, appKey: item.itemKey } });
    },
    [navigate, state]
  );

  // Helper function to find appIdent from groups by appId
  const findAppIdentByAppId = useCallback(
    (appId: string, _groups: any[]): string => {
      for (const group of _groups) {
        const item = group.items?.find(
          (y: AppMenuItemType) => y.appId === appId
        );
        if (item && item.itemAppIdent) {
          return item.itemAppIdent;
        }
      }
      return "";
    },
    []
  );

  const handleSelectedAppChanged = useCallback(
    (item: AppTabContainerItemType, data: string = "") => {
      // Find the Application object from apps list
      const appObject = apps.find((app) => app.id === item.appId);
      if (appObject) {
        dispatch(selectApp(appObject));
      } else {
        // Fallback to ID only for backward compatibility
        dispatch(selectApp(item.appId));
      }
      setSelectedAppKey(item.itemKey);
      let _item = openedAppsRef.current.find((x) => x.itemKey === item.itemKey);
      if (_item) {
        moveToApp(_item, data);
      }
    },
    [dispatch, moveToApp, apps]
  );

  const handleAppClicked = useCallback(
    (item: AppMenuItemType, data: string = "") => {
      let ts = new Date().getTime();
      let new_item = {
        itemKey: "" + ts,
        label: item.itemTitle!,
        ref: item.itemLink!,
        icon: item.itemIcon!,
        appId: item.appId!,
      };
      dispatch(addAppInfo("" + ts));
      if (item.itemAppIdent) {
        dispatch(selectApp(item.itemAppIdent));
      }
      setOpenedApps((prev) => {
        return [...prev, new_item];
      });
      setIsMenuOpen(false);
      handleSelectedAppChanged(new_item);
      moveToApp(new_item, data);
    },
    [dispatch, handleSelectedAppChanged, moveToApp]
  );

  const showTabIfMissing = useCallback(
    (_groups: any[], dataForLink: string) => {
      // Check if navigation state has appKey for precise matching
      const stateAppKey = state?.appKey;

      let existingTab;
      if (stateAppKey) {
        // If we have appKey in state, use it for precise matching
        existingTab = openedAppsRef.current.find(
          (x) => x.itemKey === stateAppKey
        );
      } else {
        // Fallback to pathname matching
        existingTab = openedAppsRef.current.find((x) => x.ref === pathname);
      }

      if (existingTab) {
        // If tab exists, just select it
        handleSelectedAppChanged(existingTab, dataForLink);
        return;
      }

      // If no tab exists for this pathname, create a new one
      let item = _groups.find((x) =>
        x.items.find((y: AppMenuItemType) => y.itemLink === pathname)
      );

      if (item) {
        handleAppClicked(
          item.items.find((y: AppMenuItemType) => y.itemLink === pathname)!,
          dataForLink
        );
      }
    },
    [pathname, state, handleAppClicked, handleSelectedAppChanged]
  );

  // Memoize applications loading logic to prevent unnecessary API calls
  const loadApplications = useCallback(async () => {
    if (applicationsLoaded.current) return; // Skip if already loaded

    try {
      const result = await featureService.listApplications(
        new ListApplicationsRequest(store.Server, store.SessionId)
      );
      store.customApps = result.APPLICATION.filter(
        (x) => x.accessType === "custom"
      ).map((x) => x.appIdent);
      let groupArrays = _MakeGroupsForAppMenu(result.APPLICATION);

      setApps(result.APPLICATION);
      setGroups(groupArrays);
      showTabIfMissing(groupArrays, search.substring(1));
      setTimeout(() => {
        if (redirectUrl) {
          console.log("redirecting to", redirectUrl);
          navigate(redirectUrl, { state: { session }, replace: true });
          setRedirectUrl("");
        }
      }, 100);
      applicationsLoaded.current = true; // Mark as loaded to prevent future calls
    } catch (error) {
      console.log("error in loadApplications", error);
    }
  }, [
    featureService,
    store,
    setApps,
    navigate,
    session,
    redirectUrl,
    setRedirectUrl,
    search,
    showTabIfMissing,
  ]);

  // Separate useEffect for applications loading - only runs once per session
  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  // Effect to create tab when navigating directly to a URL
  useEffect(() => {
    // Only run if groups are loaded and we're not already on dashboard
    if (groups.length === 0 || pathname === "/user/dashboard") return;

    // Check if navigation state has appKey for precise matching
    const stateAppKey = state?.appKey;

    let existingTab;
    if (stateAppKey) {
      // If we have appKey in state, use it for precise matching
      existingTab = openedAppsRef.current.find(
        (x) => x.itemKey === stateAppKey
      );
    } else {
      // Fallback to pathname matching
      existingTab = openedAppsRef.current.find((x) => x.ref === pathname);
    }

    if (existingTab) {
      // If tab exists, select it and update the URL with query params if needed
      if (selectedAppKey !== existingTab.itemKey) {
        handleSelectedAppChanged(existingTab, search.substring(1));
      }
      return;
    }

    // If no tab exists for this pathname, create one
    let item = groups.find((x) =>
      x.items.find((y: AppMenuItemType) => y.itemLink === pathname)
    );
    if (item) {
      const appItem = item.items.find(
        (y: AppMenuItemType) => y.itemLink === pathname
      );
      if (appItem) {
        handleAppClicked(appItem, search.substring(1));
      }
    }
  }, [
    pathname,
    search,
    groups,
    selectedAppKey,
    state,
    handleAppClicked,
    handleSelectedAppChanged,
  ]);

  const findAppByAppIdent = useCallback(
    (appIdent: string, data: string = "") => {
      let item = groups.find((x) =>
        x.items.find((y: AppMenuItemType) => y.itemAppIdent === appIdent)
      );

      if (item) {
        handleAppClicked(
          item.items.find((y: AppMenuItemType) => y.itemAppIdent === appIdent),
          data
        );
      }
    },
    [groups, handleAppClicked]
  );

  const handleFinderDetailLoaded = useCallback(
    (event: AtollonEvent) => {
      findAppByAppIdent(
        "com.atollon.core.finder",
        `id=${event.detail.contextId}`
      );
    },
    [findAppByAppIdent]
  );

  const handleContactsAppLoaded = useCallback(
    (event: AtollonEvent) => {
      findAppByAppIdent(
        "com.atollon.core.contact",
        `id=${event.detail.contactId}`
      );
    },
    [findAppByAppIdent]
  );

  const handleNewMessageCreating = (event: AtollonEvent) => {
    // Generate a unique ID for this dialog instance
    const dialogId = `message-dialog-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 11)}`;
    //console.log('event.detail.attachments', event);
    // Add a new dialog to the array
    setMessageDialogs((prev) => [
      ...prev,
      {
        id: dialogId,
        itemId: event.detail.contextId,
        attachments: event.detail.attachments,
        notifyUsers: event.detail.notifyUsers,
        objMessage: event.detail.messageObj,
        replyType: event.detail.replyType,
        emailAttachments: event.detail.emailAttachments,
        isFolderContext: event.detail.isFolderContext,
      },
    ]);
  };

  const handleNewMessageCreated = (event: any) => {
    // Store flag in sessionStorage so MessageListComponent can check when it mounts
    const contextId = event.detail?.contextId || "";
    sessionStorage.setItem("messageListNeedsReload", "true");
    sessionStorage.setItem("messageListReloadContext", contextId);
  };

  // Add function to close a specific message dialog
  const closeMessageDialog = (dialogId: string) => {
    setMessageDialogs((prev) =>
      prev.filter((dialog) => dialog.id !== dialogId)
    );
  };

  useEffect(() => {
    subscribe(AtollonEvent.FINDER_DETAIL_LOADED, handleFinderDetailLoaded);
    subscribe(AtollonEvent.NEW_MESSAGE_CREATING, handleNewMessageCreating);
    subscribe(AtollonEvent.NEW_MESSAGE_CREATED, handleNewMessageCreated);
    subscribe(AtollonEvent.CONTACTS_APP_LOADED, handleContactsAppLoaded);
    subscribe(AtollonEvent.REPLY_MESSAGE, handleNewMessageCreating);

    return () => {
      unsubscribe(AtollonEvent.FINDER_DETAIL_LOADED, handleFinderDetailLoaded);
      unsubscribe(AtollonEvent.NEW_MESSAGE_CREATING, handleNewMessageCreating);
      unsubscribe(AtollonEvent.NEW_MESSAGE_CREATED, handleNewMessageCreated);
      unsubscribe(AtollonEvent.CONTACTS_APP_LOADED, handleContactsAppLoaded);
      unsubscribe(AtollonEvent.REPLY_MESSAGE, handleNewMessageCreating);
    };
  }, [handleFinderDetailLoaded, handleContactsAppLoaded]);

  const handleSessionExpired = async () => {
    if (is_login_in_progress.current) return;
    is_login_in_progress.current = true;

    if (store.Username && store.Password && store.Server) {
      ToastQueue.info("Session expired. Logging in again...", {
        timeout: 3000,
      });
      let p_username: string = store.Username;
      if (!store.Username.includes("@")) {
        p_username += "@" + loginInfo.instanceName;
      }

      await doLogin(
        p_username,
        store.Password,
        store.Server,
        mainService,
        store
      );
      navigate("/user/dashboard", { state: { session }, replace: true });
    } else {
      if (showDialog !== LOGIN_DIALOG) {
        setShowDialog(LOGIN_DIALOG);
      }
    }
    is_login_in_progress.current = false;
  };

  ServiceBase.SessionExpiredHandler = handleSessionExpired;

  const handleClick = (e: any) => {
    setIsMenuOpen(false);
  };

  const closeCurrentApp = () => {
    setOpenedApps((prev) => {
      let index = prev.findIndex((x) => x.itemKey === selectedAppKey);
      if (index !== -1) {
        dispatch(removeAppInfo(selectedAppKey));
        const updated = prev.filter((_, i) => i !== index);

        if (updated.length > 0) {
          setTimeout(() => handleSelectedAppChanged(updated[0]), 0);
        } else {
          navigate("/user/dashboard", { state: state });
        }
        return updated;
      }
      return prev;
    });
  };

  const handleInfoChange = useCallback(
    (e: AtollonEvent) => {
      setOpenedApps((prev) => {
        let item = prev.find((x) => x.itemKey === selectedAppKey);
        if (item && item.ref === "/finder") {
          const updated = prev.map((app) =>
            app.itemKey === selectedAppKey ? { ...app, altName: e.detail } : app
          );

          return updated;
        }
        return prev;
      });
    },
    [selectedAppKey]
  );

  const handleHomeClicked = () => {
    navigate("/user/dashboard", { state: { session }, replace: true });
  };

  useEffect(() => {
    subscribe(AtollonEvent.INFO_CHANGED, handleInfoChange);
    return () => {
      unsubscribe(AtollonEvent.INFO_CHANGED, handleInfoChange);
    };
  }, [handleInfoChange]);

  useKeyboardShortcuts([
    ...addKey("g", { ctrl: true }, () => setIsShowSearch(true)),

    ...addKey("a", { ctrl: true, shift: true }, () => setIsMenuOpen(true)),
    ...addKey("z", { ctrl: true, shift: true }, () => closeCurrentApp()),
    ...addKey("c", { ctrl: true, shift: true }, () =>
      findAppByAppIdent("com.atollon.messaging.messages")
    ),
    ...addKey("o", { ctrl: true, shift: true }, () =>
      findAppByAppIdent("com.atollon.core.contact")
    ),
    ...addKey("s", { ctrl: true, shift: true }, () =>
      findAppByAppIdent("com.atollon.scheduler.events")
    ),
    ...addKey("u", { ctrl: true, shift: true }, () =>
      findAppByAppIdent("com.atollon.scheduler.tasks")
    ),
    ...addKey("f", { ctrl: true, shift: true }, () =>
      findAppByAppIdent("com.atollon.core.finder")
    ),
    ...addKey("d", { ctrl: true, shift: true }, () =>
      findAppByAppIdent("com.atollon.core.document")
    ),
    ...addKey("x", { ctrl: true, shift: true }, () => handleLogout()),
    ...addKey("h", { ctrl: true }, () => handleHomeClicked()),
    ...addKey("/", { ctrl: true }, () => setShowDialog(HELP_DIALOG)),
  ]);

  const handleAppClosed = (item: string) => {
    setOpenedApps((prev) => {
      let index = prev.findIndex((x) => x.itemKey === item);
      if (index !== -1) {
        dispatch(removeAppInfo(item));
        const updated = prev.filter((_, i) => i !== index);

        if (item === selectedAppKey) {
          if (updated.length > 0) {
            setTimeout(() => handleSelectedAppChanged(updated[0]), 0);
          } else {
            navigate("/user/dashboard", { state: state });
          }
        }
        return updated;
      }
      return prev;
    });
  };

  const handleHelpClicked = () => {
    setShowDialog(HELP_DIALOG);
  };
  const updateFavouriteContextCallback = useCallback(
    async (contextId: number, contextType: string) => {
      try {
        var folderId = contextType === "FOLDER" ? contextId : undefined;
        var projectId = contextType === "PROJECT" ? contextId : undefined;
        var activityId = contextType === "ACTIVITY" ? contextId : undefined;

        await projectService.updateFavouriteContext(
          new UpdateFavouriteContextRequest(
            store.Server,
            store.SessionId,
            folderId,
            projectId,
            activityId
          )
        );
      } catch (error) {
        console.log(error);
      }
    },
    [projectService, store.Server, store.SessionId]
  );

  const handleSearchSelected = async (item: any) => {
    setIsShowSearch(false);
    await updateFavouriteContextCallback(item.contextId, item.contextType);
    invokeFinderApp(item.contextId);
  };

  const handleLogout = async () => {
    ToastQueue.info("Are you sure to want to logout?", {
      actionLabel: "Yes",
      onAction: async () => {
        try {
          await mainService.logout(
            new LogoutRequest(store.SessionId, store.Server)
          );
          await OneTimeStorage.getInstance().clear();
          store.clear();
        } catch (error) {
          console.log("error in handleMenuAction", error);
        }
        navigate("/");
      },
      shouldCloseOnAction: true,
    });
  };

  return (
    <div className={styles.mainContainer} onClick={handleClick}>
      <DialogContainer
        onDismiss={() => {
          setShowDialog("");
        }}
      >
        {showDialog === LOGIN_DIALOG && <LoginDialog />}
        {showDialog === HELP_DIALOG && (
          <HelpDialog close={() => setShowDialog("")} />
        )}
      </DialogContainer>
      {messageDialogs.map((dialog, index) => (
        <NewMessageDialog
          windowIndex={index}
          key={dialog.id}
          selectedItemId={dialog.itemId}
          close={() => closeMessageDialog(dialog.id)}
          attachments={dialog.attachments}
          notifyUsers={dialog.notifyUsers}
          objMessage={dialog.objMessage}
          replyType={dialog.replyType}
          emailAttachments={dialog.emailAttachments}
          isFolderContext={dialog.isFolderContext}
        />
      ))}
      <Grid
        areas={{
          base: ["header", "main"],
          M: ["header", "main"],
        }}
        columns={["1fr"]}
        rows={{
          base: ["auto", "1fr"],
          M: ["auto", "1fr"],
        }}
        height={{
          base: "91vh",
          M: "100vh",
        }}
      >
        <View gridArea="header" backgroundColor={"static-white"}>
          <View isHidden={{ base: true, M: false }}>
            <UserHeader
              groups={groups}
              isMenuOpen={isMenuOpen}
              setIsMenuOpen={setIsMenuOpen}
              isSearchOpen={isShowSearch}
              setIsSearchOpen={setIsShowSearch}
              onAppClicked={handleAppClicked}
              closeApp={handleAppClosed}
              folderStatusMap={folderStatusMapping}
              projectStatusMap={projectStatusMapping}
              activityStatusMap={activityStatusMapping}
              openedApps={openedApps}
              selectedApp={selectedAppKey}
              onSelectedAppChanged={handleSelectedAppChanged}
              onHelpClicked={handleHelpClicked}
              onLogout={handleLogout}
            />
          </View>
          <View isHidden={{ base: true, M: true }}>
            <UserHeaderMobile />
          </View>
        </View>
        <View gridArea="main" UNSAFE_style={{ overflow: "auto" }}>
          <Outlet />
        </View>
      </Grid>
      <View isHidden={{ base: false, M: true }}>
        {isMenuOpen && (
          <div className={styles.mobileAppScreen}>
            <AppMenuMobile
              openedApps={openedApps}
              groups={groups}
              onItemClicked={handleAppClicked}
              selectedApp={selectedAppKey}
              onSelectedAppChanged={handleSelectedAppChanged}
            />
          </div>
        )}
        {isProfile && (
          <div className={styles.mobileAppScreen}>
            <Profile onLogout={handleLogout} />
          </div>
        )}
        {isCommMenuOpen && (
          <MessageCountProvider>
            <CommunicationMenu
              showMenu={isCommMenuOpen}
              setShowMobileMenu={setShowMobileMenu}
              setIsCommMenuOpen={setIsCommMenuOpen}
            />
          </MessageCountProvider>
        )}
        {isShowSearch && (
          <div className={styles.mobileAppScreen}>
            <MobileGlobalSearch
              close={() => setIsShowSearch(false)}
              onSearchSelected={handleSearchSelected}
              folderStatusMap={folderStatusMapping}
              projectStatusMap={projectStatusMapping}
              activityStatusMap={activityStatusMapping}
            />
          </div>
        )}
      </View>
      <div
        className={styles.mobileMenu}
        onClick={(e: any) => e.stopPropagation()}
      >
        {showMobileMenu && (
          <MobileMenu
            setIsMenuOpen={setIsMenuOpen}
            setIsShowSearch={setIsShowSearch}
            setIsProfileOpen={setIsProfile}
            setIsCommMenuOpen={setIsCommMenuOpen}
          />
        )}
      </div>
    </div>
  );
}

export default UserWebLayout;
